import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";
import { CURRENCY_SYMBOLS, EXCHANGE_RATES } from "@/lib/currencyConversion";

const SUPPORTED_CURRENCIES = Object.keys(EXCHANGE_RATES) as Array<keyof typeof EXCHANGE_RATES>;

interface Income {
  id: string;
  amount: number;
  source: string;
  category: string | null;
  income_date: string;
  note: string | null;
  currency: string;
}

interface IncomeEditDialogProps {
  income: Income | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export const IncomeEditDialog = ({ income, open, onOpenChange, onUpdate }: IncomeEditDialogProps) => {
  const { t } = useLanguage();
  const [amount, setAmount] = useState(income?.amount.toString() || "");
  const [source, setSource] = useState(income?.source || "");
  const [note, setNote] = useState(income?.note || "");
  const [incomeDate, setIncomeDate] = useState(income?.income_date || new Date().toISOString().split('T')[0]);
  const [currency, setCurrency] = useState(income?.currency || "EUR");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (income) {
      setAmount(income.amount.toString());
      setSource(income.source);
      setNote(income.note || "");
      setIncomeDate(income.income_date);
      setCurrency(income.currency || "EUR");
    }
  }, [income]);

  const handleSave = async () => {
    if (!income) return;
    
    setIsSaving(true);
    try {
      const parsedAmount = parseFloat(amount);
      
      if (isNaN(parsedAmount) || parsedAmount <= 0 || parsedAmount > 10000000) {
        throw new Error('Invalid amount');
      }

      if (!source.trim()) {
        throw new Error('Source is required');
      }

      const { error } = await supabase
        .from('income')
        .update({
          amount: parsedAmount,
          source: source.trim(),
          note: note.trim() || null,
          income_date: incomeDate,
          currency: currency,
        })
        .eq('id', income.id);

      if (error) throw error;

      trackEvent({ name: 'edit_saved' });
      
      toast({
        title: t('income.updated') || 'Income updated',
      });

      onUpdate();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!income) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('income.edit') || 'Edit Income'}</SheetTitle>
        </SheetHeader>
        
        <div className="space-y-4 py-6">
          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('expense.amount')}</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-12 text-lg"
            />
          </div>

          {/* Currency */}
          <div className="space-y-2">
            <Label>{t('expense.currency')}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map((curr) => (
                  <SelectItem key={curr} value={curr}>
                    {CURRENCY_SYMBOLS[curr as keyof typeof CURRENCY_SYMBOLS] || curr} {curr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="date">{t('expense.date')}</Label>
            <Input
              id="date"
              type="date"
              value={incomeDate}
              onChange={(e) => setIncomeDate(e.target.value)}
              className="h-12"
            />
          </div>

          {/* Source */}
          <div className="space-y-2">
            <Label htmlFor="source">{t('income.source') || 'Source'}</Label>
            <Input
              id="source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g., Salary, Freelance, Refund"
              className="h-12"
            />
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">{t('expense.noteOptional')}</Label>
            <Input
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('expense.addNote')}
              className="h-12"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-4">
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="flex-1 min-h-[44px]"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="min-h-[44px]"
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
